import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { type ErrorNotification } from "@via/utils/interfaces.tsx";
import type {
  VmetaImportApiFieldsResult,
  VmetaImportConfigCreateBody,
  VmetaImportConfigDetail,
  VmetaImportConfigListItem,
  VmetaImportConfigUpdateBody,
  VmetaImportLogin,
  VmetaImportLoginCreateBody,
  VmetaImportLoginKeyBody,
  VmetaImportLoginKeyResult,
  VmetaImportLoginList,
  VmetaImportLoginUpdateBody,
  VmetaImportPreviewStart,
  VmetaImportPreviewState,
  VmetaImportResponse,
  VmetaImportRunProblems,
  VmetaImportTestLoginResult,
} from "common";
import { previewRefetchInterval } from "@via/pages/projects/metadata/configuration/data-import/utils/form";

export const IMPORT_CONFIGS_KEY = "/api/v1/vmeta/canvas/import-config";
const base = (canvasId: string) =>
  `/api/v1/vmeta/canvas/${canvasId}/import-config`;
type IdVars = { canvasId: string; id: string };

/** The server's fixed message (a refusal reason, 409, 429), or the fallback. */
export const errorMessage = (error: unknown, fallback: string): string =>
  (error as ErrorNotification)?.response?.data?.message || fallback;

export const useImportConfigs = (
  canvasId: string,
  enabled: boolean
): UseQueryResult<VmetaImportConfigListItem[], Error> =>
  useQuery({
    queryKey: [IMPORT_CONFIGS_KEY, canvasId],
    queryFn: async () =>
      (
        await axios.get<VmetaImportResponse<VmetaImportConfigListItem[]>>(
          base(canvasId)
        )
      ).data.data,
    enabled: enabled && !!canvasId,
    retry: false,
  });

export const useImportConfig = (
  canvasId: string,
  id: string | null
): UseQueryResult<VmetaImportConfigDetail, Error> =>
  useQuery({
    queryKey: [IMPORT_CONFIGS_KEY, canvasId, id],
    queryFn: async () =>
      (
        await axios.get<VmetaImportResponse<VmetaImportConfigDetail>>(
          `${base(canvasId)}/${id}`
        )
      ).data.data,
    enabled: !!canvasId && !!id,
    retry: false,
  });

/** A run's problems load only once its row is expanded. */
export const useImportRunProblems = (
  canvasId: string,
  id: string,
  runId: string | null,
  enabled: boolean
): UseQueryResult<VmetaImportRunProblems, Error> =>
  useQuery({
    queryKey: [IMPORT_CONFIGS_KEY, canvasId, id, "run-problems", runId],
    queryFn: async () =>
      (
        await axios.get<VmetaImportResponse<VmetaImportRunProblems>>(
          `${base(canvasId)}/${id}/runs/${runId}/problems`
        )
      ).data.data,
    enabled: enabled && !!canvasId && !!id && !!runId,
    retry: false,
  });

const useInvalidateList = () => {
  const queryClient = useQueryClient();
  return (canvasId: string) =>
    queryClient.invalidateQueries({ queryKey: [IMPORT_CONFIGS_KEY, canvasId] });
};

/**
 * Save errors are shown beside the form, so create and update raise no notification. Their
 * variables can carry a typed password, so gcTime 0 drops each finished mutation from the
 * MutationCache at once instead of keeping it for the default five minutes.
 */
export const useCreateImportConfig = () => {
  const invalidate = useInvalidateList();
  return useMutation<
    VmetaImportConfigDetail,
    ErrorNotification,
    { canvasId: string; body: VmetaImportConfigCreateBody }
  >({
    mutationFn: async ({ canvasId, body }) =>
      (
        await axios.post<VmetaImportResponse<VmetaImportConfigDetail>>(
          base(canvasId),
          body
        )
      ).data.data,
    gcTime: 0,
    onSuccess: async (_, { canvasId }) => invalidate(canvasId),
  });
};

export const useUpdateImportConfig = () => {
  const invalidate = useInvalidateList();
  return useMutation<
    VmetaImportConfigDetail,
    ErrorNotification,
    IdVars & { body: VmetaImportConfigUpdateBody }
  >({
    mutationFn: async ({ canvasId, id, body }) =>
      (
        await axios.patch<VmetaImportResponse<VmetaImportConfigDetail>>(
          `${base(canvasId)}/${id}`,
          body
        )
      ).data.data,
    gcTime: 0,
    onSuccess: async (_, { canvasId }) => invalidate(canvasId),
  });
};

export const useDeleteImportConfig = () => {
  const invalidate = useInvalidateList();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<void, ErrorNotification, IdVars>({
    mutationFn: async ({ canvasId, id }) => {
      await axios.delete(`${base(canvasId)}/${id}`);
    },
    onSuccess: async (_, { canvasId }) => {
      await invalidate(canvasId);
      setNotification({
        id: "delete-import-config-success",
        message: "Import deleted",
        details: "The data import has been deleted.",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: error => {
      setNotification({
        id: "delete-import-config-failed",
        message: "Import not deleted",
        details: errorMessage(error, "The data import could not be deleted."),
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useTestImportLogin = () =>
  useMutation<VmetaImportTestLoginResult, ErrorNotification, IdVars>({
    mutationFn: async ({ canvasId, id }) =>
      (
        await axios.post<VmetaImportResponse<VmetaImportTestLoginResult>>(
          `${base(canvasId)}/${id}/test-login`
        )
      ).data.data,
  });

export const useLoadImportApiFields = () =>
  useMutation<VmetaImportApiFieldsResult, ErrorNotification, IdVars>({
    mutationFn: async ({ canvasId, id }) =>
      (
        await axios.post<VmetaImportResponse<VmetaImportApiFieldsResult>>(
          `${base(canvasId)}/${id}/api-fields`
        )
      ).data.data,
  });

export const useStartImportPreview = () =>
  useMutation<VmetaImportPreviewStart, ErrorNotification, IdVars>({
    mutationFn: async ({ canvasId, id }) =>
      (
        await axios.post<VmetaImportResponse<VmetaImportPreviewStart>>(
          `${base(canvasId)}/${id}/preview`
        )
      ).data.data,
  });

export const useImportPreview = (
  canvasId: string,
  id: string,
  previewId: string | null
): UseQueryResult<VmetaImportPreviewState, Error> =>
  useQuery({
    queryKey: [IMPORT_CONFIGS_KEY, canvasId, id, "preview", previewId],
    queryFn: async () =>
      (
        await axios.get<VmetaImportResponse<VmetaImportPreviewState>>(
          `${base(canvasId)}/${id}/preview/${previewId}`
        )
      ).data.data,
    enabled: !!previewId,
    retry: false,
    refetchInterval: query =>
      previewRefetchInterval(query.state.data, query.state.status === "error"),
  });

export const IMPORT_LOGINS_KEY = "/api/v1/vmeta/import-credentials";
const loginBase = "/api/v1/vmeta/import-credentials";

export const useImportLogins = (
  enabled: boolean
): UseQueryResult<VmetaImportLoginList, Error> =>
  useQuery({
    queryKey: [IMPORT_LOGINS_KEY],
    queryFn: async () =>
      (await axios.get<VmetaImportResponse<VmetaImportLoginList>>(loginBase))
        .data.data,
    enabled,
    retry: false,
  });

const useInvalidateLogins = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [IMPORT_LOGINS_KEY] });
};

/** Variables can carry a typed password, so gcTime 0 drops each finished mutation at once. */
export const useCreateImportLogin = () => {
  const invalidate = useInvalidateLogins();
  return useMutation<
    VmetaImportLogin,
    ErrorNotification,
    VmetaImportLoginCreateBody
  >({
    mutationFn: async body =>
      (await axios.post<VmetaImportResponse<VmetaImportLogin>>(loginBase, body))
        .data.data,
    gcTime: 0,
    onSuccess: async () => invalidate(),
  });
};

export const useUpdateImportLogin = () => {
  const invalidate = useInvalidateLogins();
  return useMutation<
    VmetaImportLogin,
    ErrorNotification,
    { id: number; body: VmetaImportLoginUpdateBody }
  >({
    mutationFn: async ({ id, body }) =>
      (
        await axios.patch<VmetaImportResponse<VmetaImportLogin>>(
          `${loginBase}/${id}`,
          body
        )
      ).data.data,
    gcTime: 0,
    onSuccess: async () => invalidate(),
  });
};

export const useDeleteImportLogin = () => {
  const invalidate = useInvalidateLogins();
  return useMutation<void, ErrorNotification, { id: number }>({
    mutationFn: async ({ id }) => {
      await axios.delete(`${loginBase}/${id}`);
    },
    onSuccess: async () => invalidate(),
  });
};

export const useGenerateImportLoginKey = () => {
  const invalidate = useInvalidateLogins();
  return useMutation<
    VmetaImportLoginKeyResult,
    ErrorNotification,
    { id: number; version: number }
  >({
    mutationFn: async ({ id, version }) =>
      (
        await axios.post<VmetaImportResponse<VmetaImportLoginKeyResult>>(
          `${loginBase}/${id}/key`,
          { version } satisfies VmetaImportLoginKeyBody
        )
      ).data.data,
    onSuccess: async () => invalidate(),
  });
};

export const useTestStoredLogin = () =>
  useMutation<VmetaImportTestLoginResult, ErrorNotification, { id: number }>({
    mutationFn: async ({ id }) =>
      (
        await axios.post<VmetaImportResponse<VmetaImportTestLoginResult>>(
          `${loginBase}/${id}/test-login`
        )
      ).data.data,
  });
